/**
 * instrumentation.ts
 *
 * OpenTelemetry instrumentation for browser application
 * Sends traces to SigNoz
 */

import type { Context } from '@opentelemetry/api';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import type { ReadableSpan, Span, SpanProcessor } from '@opentelemetry/sdk-trace-base';
import { BatchSpanProcessor, SimpleSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';

export type TelemetryUser = {
    id?: string | number;
    email?: string;
    username?: string;
};

let telemetryUser: TelemetryUser | null = null;

/**
 * Set the current "end user" info for tracing.
 * This will be attached as span attributes on new spans.
 */
export function setTelemetryUser(user: TelemetryUser | null) {
    telemetryUser = user;
}

class UserAttributesSpanProcessor implements SpanProcessor {
    constructor(private readonly next: SpanProcessor) {}

    onStart(span: Span, parentContext: Context): void {
        const user = telemetryUser;
        if (user) {
            if (user.id != null) span.setAttribute('enduser.id', String(user.id));
            if (user.email) span.setAttribute('enduser.email', user.email);
            if (user.username) span.setAttribute('enduser.username', user.username);
        }
        this.next.onStart(span, parentContext);
    }

    onEnd(span: ReadableSpan): void {
        this.next.onEnd(span);
    }

    shutdown(): Promise<void> {
        return this.next.shutdown();
    }

    forceFlush(): Promise<void> {
        return this.next.forceFlush();
    }
}

// SigNoz OTLP endpoint configuration
const otelUrl = import.meta.env.VITE_OTEL_URL || 'http://localhost:4318';

const signozEndpoint = otelUrl.endsWith('/v1/traces') ? otelUrl : `${otelUrl}/v1/traces`;

const serviceName = import.meta.env.VITE_OTEL_SERVICE || 'bluecrow-app';
const serviceVersion = import.meta.env.VITE_APP_VERSION || '0.0.0';

// Only initialize if enabled
if (import.meta.env.VITE_OTEL_ENABLED !== 'false') {
    try {
        // Create OTLP exporter for SigNoz
        const exporter = new OTLPTraceExporter({
            url: signozEndpoint,
            headers: {},
        });

        // Create resource with service information
        const resource = resourceFromAttributes({
            [ATTR_SERVICE_NAME]: serviceName,
            [ATTR_SERVICE_VERSION]: serviceVersion,
        });

        // Use SimpleSpanProcessor for immediate export (better for debugging)
        // Switch to BatchSpanProcessor in production for better performance
        const useBatchProcessor = import.meta.env.VITE_OTEL_BATCH !== 'false';
        const spanProcessor: SpanProcessor = useBatchProcessor
            ? new BatchSpanProcessor(exporter, {
                maxExportBatchSize: 512,
                scheduledDelayMillis: 5000,
                exportTimeoutMillis: 30000,
            })
            : new SimpleSpanProcessor(exporter);

        // Create tracer provider
        const provider = new WebTracerProvider({
            resource,
            spanProcessors: [new UserAttributesSpanProcessor(spanProcessor)],
        });

        // Register the provider with ZoneContextManager for async support
        provider.register({
            contextManager: new ZoneContextManager(),
        });

        // Register instrumentations
        registerInstrumentations({
            instrumentations: [
                new DocumentLoadInstrumentation(),
            ],
        });

        // Test the connection by creating a test span
        const tracer = provider.getTracer('bluecrow-app');
        const testSpan = tracer.startSpan('otel-init-test');
        testSpan.end();
    } catch (error) {
        console.error('Failed to initialize OpenTelemetry:', error);
    }
}
